import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, RefreshCw, Download, Filter, Calendar, FileText, User, Hash, CheckCircle, ChevronDown, Receipt, Calculator } from 'lucide-react';
import MainService from '../services/mainservice';
import { useToast } from '../contexts/ToastContext';
import * as XLSX from 'xlsx';

const VATReport = () => {
  const { showError, showSuccess } = useToast();
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [vatReports, setVatReports] = useState([]);
  const [allVatReports, setAllVatReports] = useState([]); // For Excel export
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [totalVATAmount, setTotalVATAmount] = useState(0);
  const [totalNetAmount, setTotalNetAmount] = useState(0);
  const observerRef = useRef();
  const [filter, setFilter] = useState({
    dateFrom: new Date().toISOString().split('T')[0],
    dateTo: new Date().toISOString().split('T')[0],
    keyword: '',
  });

  useEffect(() => {
    loadVATReports();
  }, []);

  const loadVATReports = useCallback(async (reset = true) => {
    if (reset) {
      setLoading(true);
      setSkip(0);
      setVatReports([]);
      setAllVatReports([]);
      setHasMore(true);
    } else {
      setIsLoadingMore(true);
    }

    const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
    if (!tokenData) {
      showError('Please login first');
      return;
    }

    const userData = JSON.parse(tokenData);

    try {
      const filterData = {
        DateFrom: new Date(filter.dateFrom),
        DateTo: new Date(filter.dateTo),
        Keyword: filter.keyword,
        ClientIds: [],
        FromMonth: new Date(filter.dateFrom).getMonth() + 1,
        FromDay: new Date(filter.dateFrom).getDate(),
        FromYear: new Date(filter.dateFrom).getFullYear(),
        ToMonth: new Date(filter.dateTo).getMonth() + 1,
        ToDay: new Date(filter.dateTo).getDate(),
        ToYear: new Date(filter.dateTo).getFullYear(),
        ClientId: userData.ClientId,
        Skip: reset ? 0 : skip,
        Take: 20
      };

      const response = await MainService.VatReportInList(filterData, userData.Token);
      
      let vatData = [];
      let totalCount = 0;
      let totalVATAmount = 0;
      let totalNetAmount = 0;

      // Handle response structure similar to CustomerReport
      if (response && response.Data && Array.isArray(response.Data)) {
        vatData = response.Data;
        totalCount = response.Total || response.Data.length;
        // Calculate totals from the data
        totalVATAmount = vatData.reduce((sum, vat) => sum + (parseFloat(vat.TAX) || 0), 0);
        totalNetAmount = vatData.reduce((sum, vat) => sum + (parseFloat(vat.ProductPrice) || 0), 0);
      } else if (Array.isArray(response)) {
        vatData = response;
        totalCount = response.length;
        totalVATAmount = vatData.reduce((sum, vat) => sum + (parseFloat(vat.TAX) || 0), 0);
        totalNetAmount = vatData.reduce((sum, vat) => sum + (parseFloat(vat.ProductPrice) || 0), 0);
      } else if (response && Array.isArray(response.data)) {
        vatData = response.data;
        totalCount = response.data.length;
        totalVATAmount = vatData.reduce((sum, vat) => sum + (parseFloat(vat.TAX) || 0), 0);
        totalNetAmount = vatData.reduce((sum, vat) => sum + (parseFloat(vat.ProductPrice) || 0), 0);
      } else {
        console.warn('Unexpected VAT response structure:', response);
        vatData = [];
        totalCount = 0;
        totalVATAmount = 0;
        totalNetAmount = 0;
      }

      if (reset) {
        setVatReports(vatData);
        setAllVatReports(vatData);
        setTotalCount(totalCount);
        setTotalVATAmount(totalVATAmount);
        setTotalNetAmount(totalNetAmount);
        showSuccess(`Loaded ${vatData.length} VAT records successfully`);
        console.log('Reset load - VAT Reports:', vatData.length, 'Total:', totalCount);
      } else {
        setVatReports(prev => {
          const newVatReports = [...prev, ...vatData];
          console.log('Appending VAT reports - Previous:', prev.length, 'New:', vatData.length, 'Total:', newVatReports.length);
          return newVatReports;
        });
        setAllVatReports(prev => {
          const newAllVatReports = [...prev, ...vatData];
          console.log('Appending to allVatReports - Previous:', prev.length, 'New:', vatData.length, 'Total:', newAllVatReports.length);
          return newAllVatReports;
        });
        // Update totals
        setTotalVATAmount(prev => prev + vatData.reduce((sum, vat) => sum + (parseFloat(vat.TAX) || 0), 0));
        setTotalNetAmount(prev => prev + vatData.reduce((sum, vat) => sum + (parseFloat(vat.ProductPrice) || 0), 0));
      }

      setSkip(prev => prev + 20);
      // Check if we have more VAT reports to load
      const currentLoadedCount = reset ? vatData.length : (allVatReports.length + vatData.length);
      const hasMoreData = currentLoadedCount < totalCount;
      setHasMore(hasMoreData);

      console.log('Pagination Debug:', {
        vatDataLength: vatData.length,
        currentLoadedCount,
        totalCount,
        hasMoreData,
        skip: skip + 20,
        remainingVATReports: totalCount - currentLoadedCount
      });
    } catch (error) {
      console.error('Error loading VAT reports:', error);
      showError('Failed to load VAT reports');
      if (reset) {
        setVatReports([]);
        setAllVatReports([]);
      }
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, [filter, skip, showError, showSuccess, allVatReports.length]);

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilter = () => {
    loadVATReports(true);
  };

  // Infinite scroll observer
  const lastVATElementRef = useCallback(node => {
    if (isLoadingMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      console.log('Observer triggered:', {
        isIntersecting: entries[0].isIntersecting,
        hasMore,
        isLoadingMore,
        currentVATReportsLength: vatReports.length
      });
      if (entries[0].isIntersecting && hasMore) {
        console.log('Loading more VAT reports...');
        loadVATReports(false);
      }
    });
    if (node) observerRef.current.observe(node);
  }, [isLoadingMore, hasMore, loadVATReports, vatReports.length]);

  const handleRefresh = () => {
    setFilter({
      dateFrom: new Date().toISOString().split('T')[0],
      dateTo: new Date().toISOString().split('T')[0],
      keyword: '',
    });
    loadVATReports(true);
  };

  const handleExportToExcel = () => {
    if (!Array.isArray(allVatReports) || allVatReports.length === 0) {
      showError('No data to export');
      return;
    }

    try {
      // Prepare data for Excel export
      const excelData = allVatReports.map((vat, index) => ({
        'S.No': index + 1,
        'Voucher No': vat.VoucherNo || 'N/A',
        'Date': formatDate(vat.DeliveryDate || vat.Date),
        'Product Price': vat.ProductPrice ? `AED ${vat.ProductPrice}` : 'N/A',
        'Tax': vat.TAX ? `AED ${vat.TAX}` : 'N/A',
        'Customer Amount': vat.CustomerAmount ? `AED ${vat.CustomerAmount}` : 'N/A',
        'Client Amount': vat.ClientAmount ? `AED ${vat.ClientAmount}` : 'N/A',
        'Pay Type': vat.PayType || 'N/A',
        'Customer': vat.Customer?.Name || vat.CustomerName || 'N/A',
        'Customer Mobile No': vat.Customer?.MobileNo1 || vat.CustomerMobileNo || 'N/A',
        'Status': vat.Status?.Name || vat.Status || 'N/A'
      }));

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'VAT Reports');

      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `VAT_Reports_${currentDate}.xlsx`;

      // Save the file
      XLSX.writeFile(wb, filename);
      showSuccess('Excel file exported successfully!');
    } catch (error) {
      console.error('Excel export error:', error);
      showError('Failed to export Excel file');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return `AED ${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercentage = (rate) => {
    if (!rate) return 'N/A';
    return `${parseFloat(rate)}%`;
  };

  const getStatusColor = (status) => {
    const statusStr = typeof status === 'object' ? status?.Name || status?.name : status;
    switch (statusStr?.toLowerCase()) {
      case 'submitted':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'processed':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="page-container bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">VAT Reports</h1>
          <p className="text-gray-600">Generate and view detailed VAT reports with comprehensive filtering</p>
        </div>

        {/* Filter Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">VAT Filters</h2>
              <p className="text-sm text-gray-600 mt-1">Filter your VAT records by date and keywords</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleFilter}
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <Filter className="w-5 h-5" />
                <span>Apply Filters</span>
              </button>
              <button
                onClick={handleRefresh}
                className="bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center space-x-2 shadow-md hover:shadow-lg"
              >
                <RefreshCw className="w-5 h-5" />
                <span>Reset</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-primary-600" />
                From Date
              </label>
              <input
                type="date"
                value={filter.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-gray-50 hover:bg-white"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-primary-600" />
                To Date
              </label>
              <input
                type="date"
                value={filter.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-gray-50 hover:bg-white"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                <Search className="w-4 h-4 mr-2 text-primary-600" />
                Keyword
              </label>
              <input
                type="text"
                placeholder="Search VAT records..."
                value={filter.keyword}
                onChange={(e) => handleFilterChange('keyword', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-gray-50 hover:bg-white placeholder-gray-400"
              />
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Records</p>
                <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Receipt className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Loaded Records</p>
                <p className="text-2xl font-bold text-gray-900">{allVatReports.length}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Tax</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(totalVATAmount)}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Calculator className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Product Price</p>
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(totalNetAmount)}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* VAT Reports Table */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">VAT Records</h2>
              <p className="text-sm text-gray-600 mt-1">Detailed view of all VAT records</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleExportToExcel}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <Download className="w-4 h-4" />
                <span>Export Excel</span>
              </button>
            </div>
          </div>

          <div className="overflow-x-auto overflow-y-visible rounded-xl border border-gray-200">
            <table className="w-full" style={{ minWidth: '1400px' }}>
              <thead>
                <tr className="bg-gradient-to-r from-gray-100 to-gray-200">
                  <th className="px-4 py-4 text-center text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">#</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Voucher No</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Date</th>
                  <th className="px-4 py-4 text-right text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Product Price</th>
                  <th className="px-4 py-4 text-right text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Tax</th>
                  <th className="px-4 py-4 text-right text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Customer Amount</th>
                  <th className="px-4 py-4 text-right text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Client Amount</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Pay Type</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Customer</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Customer Mobile No</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="11" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                          <RefreshCw className="w-6 h-6 animate-spin text-primary-600" />
                        </div>
                        <p className="text-lg font-medium text-gray-700">Loading VAT reports...</p>
                        <p className="text-sm text-gray-500 mt-1">Please wait while we fetch your data</p>
                      </div>
                    </td>
                  </tr>
                ) : !Array.isArray(vatReports) || vatReports.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <Receipt className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-lg font-medium text-gray-700">No VAT records found</p>
                        <p className="text-sm text-gray-500 mt-1">Try adjusting your filters to see more results</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  vatReports.map((vat, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-all duration-200 group border-b border-gray-100"
                      ref={index === vatReports.length - 1 ? lastVATElementRef : null}
                    >
                      <td className="px-4 py-4 text-center text-sm text-gray-500 font-medium border-r border-gray-100">
                        {index + 1}
                      </td>
                      <td className="px-4 py-4 text-sm font-bold text-gray-900 group-hover:text-primary-700 border-r border-gray-100">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                          <span>{vat.VoucherNo || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 border-r border-gray-100">
                        {formatDate(vat.DeliveryDate || vat.Date)}
                      </td>
                      <td className="px-4 py-4 text-sm font-bold text-blue-600 text-right border-r border-gray-100">
                        {formatCurrency(vat.ProductPrice)}
                      </td>
                      <td className="px-4 py-4 text-sm font-bold text-red-600 text-right border-r border-gray-100">
                        {formatCurrency(vat.TAX)}
                      </td>
                      <td className="px-4 py-4 text-sm font-bold text-green-600 text-right border-r border-gray-100">
                        {formatCurrency(vat.CustomerAmount)}
                      </td>
                      <td className="px-4 py-4 text-sm font-bold text-purple-600 text-right border-r border-gray-100">
                        {formatCurrency(vat.ClientAmount)}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 border-r border-gray-100">
                        {vat.PayType || 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 border-r border-gray-100">
                        {vat.Customer?.Name || vat.CustomerName || 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 border-r border-gray-100">
                        {vat.Customer?.MobileNo1 || vat.CustomerMobileNo || 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${getStatusColor(vat.Status)}`}>
                          {typeof vat.Status === 'object' ? vat.Status?.Name || 'N/A' : vat.Status || 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Loading More Indicator */}
          {isLoadingMore && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mr-2"></div>
              <span className="text-gray-600">Loading more VAT reports...</span>
            </div>
          )}

          {/* Load More Button (Fallback) */}
          {hasMore && vatReports.length > 0 && !isLoadingMore && (
            <div className="text-center py-4">
              <button
                onClick={() => loadVATReports(false)}
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200"
              >
                Load More VAT Reports ({totalCount - allVatReports.length} remaining)
              </button>
            </div>
          )}

          {/* End of Results */}
          {!hasMore && vatReports.length > 0 && !isLoadingMore && (
            <div className="text-center py-4 text-gray-500 text-sm">
              No more VAT reports to load
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default VATReport;
